import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isHTTPError } from 'ky';

import { cockpitApi } from '../cockpit.api.js';
import { cockpitQueryKeys } from '../cockpit.query-keys.js';

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

/**
 * The user's favorite trial bars (#7, REQ-VIS-005). Configuration-like data →
 * staleTime 5 min; every bar/item mutation invalidates the key, so the row
 * refreshes right after the definition window saves.
 */
export function useTrialBars(enabled = true) {
  return useQuery({
    queryKey: cockpitQueryKeys.bars(),
    queryFn: () => cockpitApi.listBars(),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });
}

/**
 * Remembers the active trial server-side (Piotr #7 odp.4) — the recall
 * follows the user across workstations. Fire-and-forget on every trial
 * switch; a failure must never block the cockpit (the user keeps working,
 * the recall just misses the last switch).
 */
export function useSaveCockpitState() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lastTrialId: number | null) => cockpitApi.saveState({ lastTrialId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cockpitQueryKeys.state() });
    },
  });
}

/**
 * The remembered last trial, read for the restore-on-load (#7 A7, Piotr
 * odp.4). Live data → staleTime 0: every cockpit mount must see the recall
 * saved moments ago on another workstation.
 */
export function useCockpitState(enabled = true) {
  return useQuery({
    queryKey: cockpitQueryKeys.state(),
    queryFn: () => cockpitApi.getState(),
    enabled,
    staleTime: 0,
    retry: queryRetry,
  });
}
